import { useState, type FormEvent } from 'react';
import { PROFILE_LIMITS } from '@/engine/config';
import { profileProblems } from '@/engine/profile';
import { buildTargets } from '@/engine/targets';
import type { ActivityLevel, GoalDirection, GoalPace, Profile, Sex } from '@/engine/types';
import { Field, inputClass, OptionGroup } from '@/components/ui/Field';
import { MedicalDisclaimer } from '@/components/MedicalDisclaimer';
import { currentConsent } from '@/features/consent/useConsent';
import { DEFAULT_PROFILE, saveProfile } from '@/features/profile/useProfile';
import { parseList } from '@/lib/preferences';
import { displayToKg, kgToDisplay, num, todayIso } from '@/lib/format';

const SEX: { value: Sex; label: string }[] = [
  { value: 'hombre', label: 'Hombre' },
  { value: 'mujer', label: 'Mujer' },
];

const ACTIVITY: { value: ActivityLevel; label: string; hint: string }[] = [
  { value: 'sedentario', label: 'Sedentario', hint: 'Sin ejercicio' },
  { value: 'ligero', label: 'Ligero', hint: '1-2 días' },
  { value: 'moderado', label: 'Moderado', hint: '3-4 días' },
  { value: 'alto', label: 'Alto', hint: '5-6 días' },
  { value: 'muy_alto', label: 'Muy alto', hint: 'Diario o trabajo físico' },
];

const GOAL: { value: GoalDirection; label: string }[] = [
  { value: 'perder', label: 'Perder' },
  { value: 'mantener', label: 'Mantener' },
  { value: 'ganar', label: 'Ganar' },
];

const PACE: { value: GoalPace; label: string }[] = [
  { value: 'suave', label: 'Suave' },
  { value: 'normal', label: 'Normal' },
  { value: 'agresivo', label: 'Rápido' },
];

/**
 * Onboarding en una sola pantalla y no en pasos: son ocho datos y un
 * asistente de cinco pantallas para eso solo añade toques.
 *
 * Los objetivos se recalculan en vivo mientras se rellena, para que se vea qué
 * cambia al mover cada cosa.
 */
export function Onboarding({ initial, fixing = false }: { initial?: Profile; fixing?: boolean }) {
  const [profile, setProfile] = useState<Profile>(initial ?? DEFAULT_PROFILE);
  const [saving, setSaving] = useState(false);
  const [allergiesText, setAllergiesText] = useState((initial?.allergies ?? []).join(', '));
  const [dislikesText, setDislikesText] = useState((initial?.dislikes ?? []).join(', '));

  function patch(changes: Partial<Profile>) {
    setProfile((p) => ({ ...p, ...changes }));
  }

  // La misma comprobación que hace `saveProfile`: lo que aquí se da por bueno
  // es exactamente lo que se puede guardar.
  const problems = profileProblems(profile, todayIso());
  const valid = problems.length === 0;
  const preview = valid ? buildTargets(profile, todayIso()) : null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      await saveProfile({
        ...profile,
        allergies: parseList(allergiesText),
        dislikes: parseList(dislikesText),
        // El consentimiento se dio antes de que existiera perfil: se guarda
        // con él para que viaje a los demás dispositivos (art. 9 RGPD).
        healthConsent: await currentConsent(),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      className="mx-auto flex w-full max-w-lg flex-col gap-6 px-4 py-6"
    >
      <header>
        <h1 className="text-2xl font-bold tracking-tight">
          {fixing ? 'Revisa tus datos' : 'Tus datos'}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {fixing
            ? 'Hay algún dato con el que no se pueden calcular tus objetivos. Corrígelo y sigue donde lo dejaste: no se ha borrado nada.'
            : 'Con esto calculamos tus objetivos. Podrás cambiarlo todo después.'}
        </p>
      </header>

      <OptionGroup
        label="Sexo"
        value={profile.sex}
        options={SEX}
        onChange={(sex) => patch({ sex })}
      />

      <Field label="Fecha de nacimiento">
        {(id) => (
          <input
            id={id}
            type="date"
            required
            max={todayIso()}
            value={profile.birthDate}
            onChange={(e) => patch({ birthDate: e.target.value })}
            className={inputClass}
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Altura (cm)">
          {(id) => (
            <input
              id={id}
              type="number"
              inputMode="numeric"
              min={PROFILE_LIMITS.heightCm.min}
              max={PROFILE_LIMITS.heightCm.max}
              required
              value={profile.heightCm}
              onChange={(e) => patch({ heightCm: Number(e.target.value) })}
              className={inputClass}
            />
          )}
        </Field>

        <Field label={`Peso (${profile.displayUnit})`}>
          {(id) => (
            <input
              id={id}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={round1(kgToDisplay(PROFILE_LIMITS.weightKg.min, profile.displayUnit))}
              max={round1(kgToDisplay(PROFILE_LIMITS.weightKg.max, profile.displayUnit))}
              required
              value={round1(kgToDisplay(profile.weightKg, profile.displayUnit))}
              onChange={(e) =>
                patch({ weightKg: displayToKg(Number(e.target.value), profile.displayUnit) })
              }
              className={inputClass}
            />
          )}
        </Field>
      </div>

      <OptionGroup
        label="Unidades"
        value={profile.displayUnit}
        options={[
          { value: 'kg', label: 'Kilos' },
          { value: 'lbs', label: 'Libras' },
        ]}
        onChange={(displayUnit) => patch({ displayUnit })}
      />

      <OptionGroup
        label="Nivel de actividad"
        value={profile.activity}
        options={ACTIVITY}
        onChange={(activity) => patch({ activity })}
        columns={1}
      />

      <OptionGroup
        label="Objetivo"
        value={profile.goal}
        options={GOAL}
        onChange={(goal) => patch({ goal })}
        columns={3}
      />

      {profile.goal !== 'mantener' ? (
        <OptionGroup
          label="Ritmo"
          value={profile.pace}
          options={PACE}
          onChange={(pace) => patch({ pace })}
          columns={3}
        />
      ) : null}

      <OptionGroup
        label="¿Eres vegetariano?"
        value={profile.vegetarian ? 'si' : 'no'}
        options={[
          { value: 'no', label: 'No' },
          { value: 'si', label: 'Sí' },
        ]}
        onChange={(v) => patch({ vegetarian: v === 'si' })}
      />

      {/* El texto se guarda tal cual se escribe y se parte al guardar: si se
          partiera en cada tecla, la coma desaparecería nada más escribirla. */}
      <Field label="Alergias" hint="Separadas por comas. Nunca se te propondrán.">
        {(id) => (
          <input
            id={id}
            type="text"
            autoComplete="off"
            placeholder="marisco, frutos secos…"
            value={allergiesText}
            onChange={(e) => setAllergiesText(e.target.value)}
            className={inputClass}
          />
        )}
      </Field>

      <Field label="Alimentos que no comes" hint="Separados por comas. Tampoco se te propondrán.">
        {(id) => (
          <input
            id={id}
            type="text"
            autoComplete="off"
            placeholder="coliflor, hígado…"
            value={dislikesText}
            onChange={(e) => setDislikesText(e.target.value)}
            className={inputClass}
          />
        )}
      </Field>

      {preview ? (
        <section
          aria-live="polite"
          className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-4"
        >
          <h2 className="text-sm font-semibold">Tus objetivos</h2>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm tabular">
            <dt className="text-[var(--color-text-muted)]">Metabolismo basal</dt>
            <dd className="text-right">{num(preview.bmr)} kcal</dd>
            <dt className="text-[var(--color-text-muted)]">Gasto estimado</dt>
            <dd className="text-right">{num(preview.tdee)} kcal</dd>
            <dt className="font-medium">Objetivo diario</dt>
            <dd className="text-right font-medium">{num(preview.kcal)} kcal</dd>
            <dt className="text-[var(--color-text-muted)]">Proteína</dt>
            <dd className="text-right">{num(preview.proteinG)} g</dd>
            <dt className="text-[var(--color-text-muted)]">Grasas</dt>
            <dd className="text-right">{num(preview.fatG)} g</dd>
            <dt className="text-[var(--color-text-muted)]">Carbohidratos</dt>
            <dd className="text-right">{num(preview.carbsG)} g</dd>
          </dl>
          {preview.kcal <= preview.bmr + 1 ? (
            <p className="mt-2 text-xs text-[var(--color-text-muted)]">
              El objetivo no baja de tu metabolismo basal, aunque el ritmo elegido pidiera más
              recorte.
            </p>
          ) : null}
        </section>
      ) : null}

      {problems.length > 0 ? (
        <ul
          aria-live="polite"
          className="flex flex-col gap-1 rounded-xl border border-[var(--color-warn)] bg-[var(--color-warn-soft)] p-3 text-sm"
        >
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}

      <MedicalDisclaimer />

      <button
        type="submit"
        disabled={!valid || saving}
        className="tap rounded-xl bg-[var(--color-brand-button)] px-4 font-semibold text-[var(--color-on-brand)] disabled:opacity-60"
      >
        {saving ? 'Guardando…' : fixing ? 'Guardar' : 'Empezar'}
      </button>
    </form>
  );
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
